import { useEffect, useState } from "react";
import { flush, trackEvent } from "../../../../utils/clientEvents";
import "./in-app-browser-banner.css";

const DISMISS_KEY = "xt_iab_banner_dismissed";

type IabKind = "facebook" | "instagram" | "tiktok" | "linkedin" | null;
type DetectionReason = "user_agent" | "referrer" | null;

function detectInAppBrowser(): {
  kind: IabKind;
  isAndroid: boolean;
  isIOS: boolean;
  reason: DetectionReason;
} {
  if (typeof navigator === "undefined") {
    return { kind: null, isAndroid: false, isIOS: false, reason: null };
  }
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  let kind: IabKind = null;
  let reason: DetectionReason = null;
  if (/FBAN|FB_IAB|FBAV/.test(ua)) kind = "facebook";
  else if (/Instagram/.test(ua)) kind = "instagram";
  else if (/TikTok/.test(ua)) kind = "tiktok";
  else if (/LinkedInApp/.test(ua)) kind = "linkedin";

  if (kind) {
    reason = "user_agent";
  } else if (isAndroid) {
    // Some Android in-app browsers present as plain Chrome/WebView but keep
    // the Meta referrer. The Samsung dropoff cohort is heavily concentrated
    // here, so use the referrer as a softer signal for the external-browser CTA.
    try {
      const host = document.referrer
        ? new URL(document.referrer).hostname.toLowerCase()
        : "";
      if (/(^|\.)(facebook|fb)\.com$/.test(host) || host === "l.facebook.com") {
        kind = "facebook";
        reason = "referrer";
      } else if (
        /(^|\.)instagram\.com$/.test(host) ||
        host === "l.instagram.com"
      ) {
        kind = "instagram";
        reason = "referrer";
      }
    } catch {
      /* ignore invalid referrers */
    }
  }
  return { kind, isAndroid, isIOS, reason };
}

function buildAndroidIntentUrl(): string {
  // Forces Android to hand the URL off to the default browser. Drops the
  // user out of the in-app browser entirely.
  const path = window.location.pathname + window.location.search;
  return `intent://${window.location.host}${path}#Intent;scheme=https;end`;
}

const InAppBrowserBanner = () => {
  const [dismissed, setDismissed] = useState(true);
  const [info, setInfo] = useState<ReturnType<typeof detectInAppBrowser>>({
    kind: null,
    isAndroid: false,
    isIOS: false,
    reason: null,
  });

  useEffect(() => {
    // Fast-lane cohort: the curtain (#initial-loading.is-fastlane,
    // client/public/index.html) is an opaque z-index-9999 overlay that
    // deliberately never lifts for Meta/IG in-app-browser visitors — the
    // static fast-lane form IS their whole experience, and this banner is
    // redundant with it. Suppress the banner entirely there (owner decision
    // 2026-07-30): no render, no events. This supersedes the earlier
    // shown-event visibility gate (#1380), which still mounted the banner
    // invisibly under the curtain. The class is stamped synchronously by the
    // inline boot script before the bundle loads, so reading it here is
    // race-free.
    const underFastLaneCurtain = !!document
      .getElementById("initial-loading")
      ?.classList.contains("is-fastlane");
    if (underFastLaneCurtain) return;

    const detected = detectInAppBrowser();
    setInfo(detected);
    if (!detected.kind) return;
    let storedDismiss = false;
    try {
      storedDismiss = sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      /* sessionStorage may be unavailable */
    }
    if (!storedDismiss) {
      setDismissed(false);
      trackEvent("iab_banner_shown", {
        kind: detected.kind,
        platform: detected.isAndroid ? "android" : detected.isIOS ? "ios" : "other",
        reason: detected.reason,
      });
    }
  }, []);

  if (dismissed || !info.kind) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    trackEvent("iab_banner_dismissed", { kind: info.kind });
  };

  const handleOpenExternal = () => {
    trackEvent("iab_banner_open_clicked", { kind: info.kind });
    flush(true);
    if (info.isAndroid) {
      window.location.href = buildAndroidIntentUrl();
    }
    // iOS in-app browsers do not allow programmatic external open. The
    // instruction text below covers iOS — there's no link to attach.
  };

  const ctaLabel = info.isAndroid ? "Open in Chrome" : "Open in browser";
  const platformHint = info.isIOS
    ? "Tap the menu (•••) above and choose “Open in Browser”."
    : info.reason === "referrer"
      ? "If this opened inside Facebook or Instagram, Chrome is usually faster and more reliable."
    : null;

  return (
    <div className="iab-banner" role="region" aria-label="External browser suggestion">
      <div className="iab-banner__body">
        <div className="iab-banner__title">
          For the best experience, open this page in your browser.
        </div>
        {platformHint ? (
          <div className="iab-banner__hint">{platformHint}</div>
        ) : null}
      </div>
      <div className="iab-banner__actions">
        {info.isAndroid ? (
          <button
            type="button"
            className="iab-banner__cta"
            onClick={handleOpenExternal}
          >
            {ctaLabel}
          </button>
        ) : null}
        <button
          type="button"
          className="iab-banner__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default InAppBrowserBanner;
